import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api';
import { usePatient } from '../../hooks/usePatient';
import { PageDataBoundary } from '../../components/PageDataBoundary/PageDataBoundary';
import { ArrowRightIcon } from '../../components/Icons';
import { ContentCard, PageContainer, PageHeading, TextButton } from '../../components';
import type { FamilyMember } from '../../lib/api';
import type { LinkedPatient } from '../../hooks/api/useLinkedPatients';
import { getPatientName } from '../../utils';

// Styled Components

const PatientSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const PatientHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.pageBackground};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const PatientNameContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const PatientName = styled.h2`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const EditButton = styled.button`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`;

const FamilyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FamilyCard = styled.button`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`;

const FamilyAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.pageBackground};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const AvatarIcon = styled.span`
  font-size: 20px;
`;

const FamilyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FamilyLineId = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FamilyRelationship = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const FamilyArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ theme }) => theme.colors.pageBackground};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.border.medium};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.md};
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 400px;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ModalInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  min-height: ${({ theme }) => theme.accessibility.minTouchTarget};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.ms};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 150%;
  cursor: pointer;
  transition: opacity 0.2s;

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.primary};
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.text.inverse};
    &:hover {
      opacity: 0.85;
    }
  `
      : `
    background: ${theme.colors.backgroundSecondary};
    border: 1px solid ${theme.colors.border.medium};
    color: ${theme.colors.text.secondary};
    &:hover {
      opacity: 0.85;
    }
  `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.status.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.md};
`;

// 各患者の家族データを表示するセクション。
// families は上位で取得したものを props 経由で受け取り、本コンポーネントでは取得しない（チラつき防止）。
const PatientFamilySection: React.FC<{
  patient: LinkedPatient;
  families: FamilyMember[];
  onFamilyClick: (lineUserId: string, patientId: number) => void;
}> = ({ patient, families, onFamilyClick }) => {
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editError, setEditError] = useState('');

  const updateDisplayNameMutation = useMutation({
    mutationFn: (displayName: string) => apiClient.updatePatientDisplayName(patient.patient_id, displayName),
    onSuccess: () => {
      // キャッシュを無効化して再取得
      queryClient.invalidateQueries({ queryKey: ['familiesByPatient', patient.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['linkedPatients'] });
      setIsEditModalOpen(false);
      setEditError('');
    },
    onError: (error: any) => {
      setEditError(error.message || '更新に失敗しました');
    },
  });

  const handleEditClick = () => {
    // patient_display_nameがあればそれを、なければpatient_nameを初期値に
    const currentDisplayName = getPatientName(patient.patient_display_name, patient.patient_name);
    setEditDisplayName(currentDisplayName);
    setEditError('');
    setIsEditModalOpen(true);
  };

  const handleSaveDisplayName = () => {
    if (!editDisplayName.trim()) {
      setEditError('表示名を入力してください');
      return;
    }
    updateDisplayNameMutation.mutate(editDisplayName.trim());
  };

  // 表示名を取得（カスタム表示名があればそれを使用、なければ正式名称）
  const displayName = getPatientName(patient.patient_display_name, patient.patient_name);

  return (
    <PatientSection>
      <PatientHeaderRow>
        <PatientNameContainer>
          <PatientName>{displayName}</PatientName>
        </PatientNameContainer>
        <EditButton onClick={handleEditClick} aria-label="表示名を編集">
          編集
        </EditButton>
      </PatientHeaderRow>

      {families.length > 0 ? (
        <FamilyList>
          {families.map((family) => (
            <FamilyCard
              key={`${family.line_user_id}-${family.patient_id}`}
              onClick={() => onFamilyClick(family.line_user_id, family.patient_id)}
            >
              <FamilyAvatar>
                <AvatarIcon>👤</AvatarIcon>
              </FamilyAvatar>
              <FamilyInfo>
                <FamilyLineId>{family.display_name || family.line_user_id}</FamilyLineId>
                <FamilyRelationship>{family.relationship || '続柄未設定'}</FamilyRelationship>
              </FamilyInfo>
              <FamilyArrow><ArrowRightIcon /></FamilyArrow>
            </FamilyCard>
          ))}
        </FamilyList>
      ) : (
        <EmptyMessage>
          この利用者に紐づく家族がまだいません
        </EmptyMessage>
      )}

      {/* 表示名編集モーダル */}
      {isEditModalOpen && (
        <ModalOverlay onClick={() => setIsEditModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>表示名を編集</ModalTitle>
            <ModalLabel htmlFor="displayName">表示名</ModalLabel>
            <ModalInput
              id="displayName"
              type="text"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              placeholder="例: おばあちゃん、母"
              maxLength={50}
            />
            {editError && <ErrorMessage>{editError}</ErrorMessage>}
            <ModalButtons>
              <ModalButton
                $variant="secondary"
                onClick={() => setIsEditModalOpen(false)}
                disabled={updateDisplayNameMutation.isPending}
              >
                キャンセル
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={handleSaveDisplayName}
                disabled={updateDisplayNameMutation.isPending}
              >
                {updateDisplayNameMutation.isPending ? '保存中...' : '保存'}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </PatientSection>
  );
};

export const FamilyListPage: React.FC = () => {
  const navigate = useNavigate();
  // linkedPatients は AppReadyGate で取得済み（PatientContext 経由でキャッシュ参照）。
  const { linkedPatients } = usePatient();

  // 全利用者の家族リストを useQueries で並列取得し、
  // すべて揃うまで本文を描画しない（セクションごとの点滅を防止）。
  const familiesQueries = useQueries({
    queries: (linkedPatients ?? []).map((patient) => ({
      queryKey: ['familiesByPatient', patient.patient_id],
      queryFn: () => apiClient.getFamiliesByPatient(patient.patient_id),
      enabled: !!apiClient.getIdToken(),
    })),
  });

  const allFamiliesReady = useMemo(() => {
    if (!linkedPatients) return false;
    if (linkedPatients.length === 0) return true;
    return familiesQueries.every((q) => q.data !== undefined || q.isError);
  }, [linkedPatients, familiesQueries]);

  const familiesByPatientId = useMemo(() => {
    const map = new Map<number, FamilyMember[]>();
    (linkedPatients ?? []).forEach((patient, index) => {
      const data = familiesQueries[index]?.data;
      map.set(patient.patient_id, data?.families ?? []);
    });
    return map;
  }, [linkedPatients, familiesQueries]);

  const handleBack = () => {
    navigate('/settings');
  };

  const handleFamilyClick = (lineUserId: string, patientId: number) => {
    navigate(`/menu/families/${lineUserId}/${patientId}`);
  };

  // 連携利用者が0件の場合の空状態（PageDataBoundary の外で表示）
  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <PageContainer>
        <PageHeading>家族一覧</PageHeading>
        <EmptyMessage>
          連携している利用者が見つかりません
        </EmptyMessage>
        <TextButton onClick={handleBack} aria-label="戻る">
          戻る
        </TextButton>
      </PageContainer>
    );
  }

  return (
    <PageDataBoundary ready={allFamiliesReady}>
      <PageContainer>
        <PageHeading>家族一覧</PageHeading>

        {linkedPatients.map((patient) => (
          <ContentCard key={patient.patient_id}>
            <PatientFamilySection
              patient={patient}
              families={familiesByPatientId.get(patient.patient_id) ?? []}
              onFamilyClick={handleFamilyClick}
            />
          </ContentCard>
        ))}

        <TextButton onClick={handleBack} aria-label="戻る">
          戻る
        </TextButton>
      </PageContainer>
    </PageDataBoundary>
  );
};

export default FamilyListPage;
